import React, { useState, useRef, useEffect } from "react";
import {
  Search, CheckCircle2, XCircle, Loader2, User,
  Tag, ChevronDown, ChevronUp, RotateCcw, ClipboardCheck,
  Sparkles, Receipt, Ticket, Phone as PhoneIcon, Copy, Check,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { visitsApi } from "@/lib/api/visits";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

const TIER_COLORS = {
  SILVER:   "bg-zinc-100 text-zinc-700",
  GOLD:     "bg-amber-100 text-amber-700",
  PLATINUM: "bg-indigo-100 text-indigo-700",
};

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch (_) { return iso.slice(0, 10); }
}

function discountLabel(offer, lang) {
  const { discount_type, discount_value, original_price } = offer;
  const en = discount_type === "PERCENTAGE"
    ? `${discount_value}% OFF`
    : `${discount_value} EGP OFF`;
  const ar = discount_type === "PERCENTAGE"
    ? `${discount_value}% خصم`
    : `${discount_value} ج.م خصم`;
  const price = original_price
    ? discount_type === "PERCENTAGE"
      ? Math.round(original_price * (1 - discount_value / 100))
      : Math.max(0, original_price - discount_value)
    : null;
  return { label: lang === "ar" ? ar : en, price };
}

export default function VerifyPage() {
  const { lang } = useI18n();
  const t = (en, ar) => lang === "ar" ? ar : en;

  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showCandidates, setShowCandidates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);     // { member, provider, active_offers }
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [offersOpen, setOffersOpen] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [logging, setLogging] = useState(false);
  const [logged, setLogged] = useState(false);
  const [loggedSavings, setLoggedSavings] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setQuery("");
    setCandidates([]);
    setShowCandidates(false);
    setResult(null);
    setSelectedOffer(null);
    setOffersOpen(false);
    setInvoiceAmount("");
    setLogged(false);
    setLoggedSavings(null);
    setCodeCopied(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Debounced typeahead search — kicks in once 2+ chars have been typed.
  useEffect(() => {
    if (result) return; // search is dormant once a member is verified
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setCandidates([]);
      setShowCandidates(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const data = await visitsApi.search(trimmed);
        if (cancelled) return;
        setCandidates(data?.items || []);
        setShowCandidates(true);
      } catch (_) {
        if (!cancelled) setCandidates([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 280);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, result]);

  const verifyByCode = async (memberCode) => {
    setLoading(true);
    setShowCandidates(false);
    setResult(null);
    setSelectedOffer(null);
    setOffersOpen(false);
    setInvoiceAmount("");
    setLogged(false);
    setLoggedSavings(null);
    try {
      const data = await visitsApi.verify(memberCode);
      setResult(data);
      if (data.active_offers?.length > 0) setOffersOpen(true);
      // Auto-select only when there's exactly one claimed offer — otherwise
      // staff must pick deliberately to avoid silently redeeming the wrong
      // claim.
      const claimedOffers = (data.active_offers || []).filter((o) => o.claim);
      if (claimedOffers.length === 1) setSelectedOffer(claimedOffers[0]);
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      toast.error(msg || t("Member not found", "العضو غير موجود"));
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (e) => {
    e?.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    // Keyboard submit: if exactly one candidate matches, pick it; otherwise
    // pass the raw query through — the backend now normalises sloppy codes.
    if (candidates.length === 1) {
      verifyByCode(candidates[0].member_code);
    } else {
      verifyByCode(trimmed);
    }
  };

  const onPickCandidate = (cand) => {
    setQuery(cand.member_code);
    verifyByCode(cand.member_code);
  };

  const copyMemberCode = () => {
    if (!result?.member?.member_code) return;
    navigator.clipboard.writeText(result.member.member_code).then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 1800);
    });
  };

  const onLogVisit = async () => {
    if (!result) return;
    setLogging(true);
    try {
      const invoiceNum = invoiceAmount === "" ? null : Number(invoiceAmount);
      const visit = await visitsApi.log({
        member_code: result.member.member_code,
        offer_id: selectedOffer?.id ?? null,
        actual_invoice_amount: invoiceNum,
        notes: null,
      });
      setLogged(true);
      setLoggedSavings(visit?.discount_amount ?? null);
      toast.success(t("Visit logged successfully!", "تم تسجيل الزيارة!"));
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      toast.error(msg || t("Failed to log visit", "فشل تسجيل الزيارة"));
    } finally {
      setLogging(false);
    }
  };

  const member = result?.member;
  // Sort claimed offers to the top so staff sees what to redeem first.
  const offers = (result?.active_offers ?? []).slice().sort((a, b) => {
    const ac = a.claim ? 1 : 0;
    const bc = b.claim ? 1 : 0;
    return bc - ac;
  });

  // Live preview of savings while staff types the invoice.
  const previewSavings = (() => {
    if (!selectedOffer || invoiceAmount === "") return null;
    const inv = Number(invoiceAmount);
    if (!Number.isFinite(inv) || inv <= 0) return null;
    if (selectedOffer.discount_type === "PERCENTAGE") {
      return Math.round(inv * Number(selectedOffer.discount_value) / 100);
    }
    if (selectedOffer.discount_type === "FIXED_PACKAGE") {
      return Math.max(0, Math.round(Number(selectedOffer.original_price || 0) - Number(selectedOffer.discount_value)));
    }
    return null;
  })();

  const requiresInvoice = selectedOffer?.discount_type === "PERCENTAGE";

  return (
    <div className="max-w-lg mx-auto space-y-6 fade-up p-1" data-testid="page-verify">
      <header>
        <h1 className="text-2xl font-bold text-foreground">{t("Verify Member", "التحقق من العضو")}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t("Enter the member's card code to verify their membership.", "أدخل كود بطاقة العضو للتحقق.")}
        </p>
      </header>

      {/* Search form */}
      <form onSubmit={onVerify} className="surface-card p-5 space-y-2 relative">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {t("Find member", "ابحث عن العضو")}
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => candidates.length > 0 && setShowCandidates(true)}
              onBlur={() => setTimeout(() => setShowCandidates(false), 150)}
              placeholder={t("Name, phone, or member code", "الاسم أو الهاتف أو كود العضو")}
              className="ps-10 text-base"
              autoFocus
              autoComplete="off"
              disabled={loading || !!result}
              data-testid="verify-code-input"
            />
            {searching && (
              <Loader2 className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
          <Button type="submit" disabled={!query.trim() || loading || !!result} data-testid="verify-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          {t(
            "Type at least 2 characters. Spaces, dashes and case don't matter.",
            "اكتب حرفين على الأقل. المسافات والشرطات وحالة الأحرف غير مهمة."
          )}
        </p>

        {/* Candidate dropdown */}
        {showCandidates && !result && candidates.length > 0 && (
          <div
            className="absolute left-5 right-5 top-full mt-1 z-20 rounded-xl border border-border bg-card shadow-lg overflow-hidden max-h-80 overflow-y-auto"
            data-testid="verify-candidates"
          >
            {candidates.map((c) => (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onPickCandidate(c)}
                className="w-full text-start px-4 py-3 hover:bg-muted/50 transition-colors flex items-center gap-3 border-b border-border/50 last:border-0"
                data-testid={`verify-candidate-${c.id}`}
              >
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold truncate">{c.full_name || "—"}</span>
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${TIER_COLORS[c.tier] || TIER_COLORS.SILVER}`}>
                      {c.tier}
                    </span>
                    {c.activation_status !== "ACTIVE" && (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-full font-semibold shrink-0 bg-amber-100 text-amber-700">
                        {t("inactive", "غير نشط")}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground">
                    <span className="font-mono">{c.member_code}</span>
                    {c.phone && (
                      <span className="inline-flex items-center gap-1">
                        <PhoneIcon className="h-2.5 w-2.5" />
                        {c.phone}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
        {showCandidates && !result && !searching && query.trim().length >= 2 && candidates.length === 0 && (
          <div className="absolute left-5 right-5 top-full mt-1 z-20 rounded-xl border border-border bg-card shadow-lg p-4 text-xs text-muted-foreground text-center">
            {t("No matches. Press the search button to try the exact code.", "لا توجد نتائج. اضغط زر البحث لتجربة الكود بالضبط.")}
          </div>
        )}
      </form>

      {/* Result card */}
      {member && (
        <div className="surface-card overflow-hidden" data-testid="verify-result-card">
          {/* Status banner */}
          <div className={`px-5 py-3 flex items-center gap-2 text-sm font-semibold ${
            member.is_expired
              ? "bg-destructive/10 text-destructive"
              : member.is_active
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400"
                : "bg-amber-50 text-amber-700"
          }`}>
            {member.is_active && !member.is_expired
              ? <CheckCircle2 className="h-4 w-4 shrink-0" />
              : <XCircle className="h-4 w-4 shrink-0" />}
            {member.is_expired
              ? t("Membership Expired", "العضوية منتهية")
              : member.is_active
                ? t("Active Member", "عضو نشط")
                : t("Membership Inactive", "العضوية غير نشطة")}
          </div>

          {/* Member info */}
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <div className="font-semibold text-foreground text-lg leading-tight" data-testid="verify-member-name">
                  {member.full_name || "—"}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <button
                    type="button"
                    onClick={copyMemberCode}
                    className="inline-flex items-center gap-1.5 font-mono text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 rounded-md px-1.5 -ms-1.5 py-0.5 transition-colors"
                    title={t("Copy code", "نسخ الكود")}
                    data-testid="verify-copy-code"
                  >
                    <span>{member.member_code}</span>
                    {codeCopied
                      ? <Check className="h-3 w-3 text-emerald-600" />
                      : <Copy className="h-3 w-3 opacity-60" />}
                  </button>
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${TIER_COLORS[member.tier] || TIER_COLORS.SILVER}`}>
                    {member.tier}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground mb-0.5">{t("Expiry Date", "تاريخ الانتهاء")}</div>
                <div className={`font-medium ${member.is_expired ? "text-destructive" : "text-foreground"}`}>
                  {fmtDate(member.expiry_date, lang)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-0.5">{t("Active Deals", "العروض المتاحة")}</div>
                <div className="font-medium text-foreground">{offers.length}</div>
              </div>
            </div>

            {/* Available offers */}
            {offers.length > 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => setOffersOpen(o => !o)}
                  className="flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                >
                  <Tag className="h-4 w-4" />
                  {t("Available Deals", "العروض المتاحة")} ({offers.length})
                  {offersOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
                {offersOpen && (
                  <div className="mt-2 space-y-2">
                    {/* "No offer" option */}
                    <label className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors hover:bg-muted/40"
                      style={{ borderColor: selectedOffer === null ? "var(--primary)" : "var(--border)" }}>
                      <input
                        type="radio"
                        name="offer"
                        className="mt-0.5 accent-primary"
                        checked={selectedOffer === null}
                        onChange={() => setSelectedOffer(null)}
                      />
                      <span className="text-sm text-muted-foreground italic">
                        {t("Visit only (no specific deal)", "زيارة فقط (بدون عرض محدد)")}
                      </span>
                    </label>

                    {offers.map(offer => {
                      const { label, price } = discountLabel(offer, lang);
                      const claimed = !!offer.claim;
                      return (
                        <label key={offer.id}
                          className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors hover:bg-muted/40 ${claimed ? "bg-emerald-50/40" : ""}`}
                          style={{ borderColor: selectedOffer?.id === offer.id ? "var(--primary)" : claimed ? "rgb(167 243 208 / 0.6)" : "var(--border)" }}>
                          <input
                            type="radio"
                            name="offer"
                            className="mt-0.5 accent-primary"
                            checked={selectedOffer?.id === offer.id}
                            onChange={() => setSelectedOffer(offer)}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-foreground leading-snug">
                                {lang === "ar" ? offer.title_ar || offer.title_en : offer.title_en || offer.title_ar}
                              </span>
                              {claimed && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                                  <Ticket className="h-2.5 w-2.5" />
                                  {t("Claimed", "تم الحجز")}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-[10px] px-1.5">{label}</Badge>
                              {price != null && (
                                <span className="text-xs text-muted-foreground">
                                  → {price.toLocaleString()} EGP
                                </span>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Invoice amount (for percentage offers) */}
            {selectedOffer && requiresInvoice && (
              <div className="rounded-xl border-2 border-primary/15 bg-primary/5 p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <Receipt className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <label className="text-xs font-bold uppercase tracking-wide text-primary">
                      {t("Actual bill amount", "قيمة الفاتورة الفعلية")}
                    </label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {t(
                        "Enter what the member is paying today, before discount.",
                        "أدخل المبلغ الذي يدفعه العضو اليوم قبل الخصم."
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    value={invoiceAmount}
                    onChange={(e) => setInvoiceAmount(e.target.value)}
                    placeholder={t("e.g. 1500", "مثال: ١٥٠٠")}
                    className="flex-1 font-mono text-base"
                    data-testid="visit-invoice-amount"
                  />
                  <span className="text-sm text-muted-foreground font-medium shrink-0">
                    {t("EGP", "ج.م")}
                  </span>
                </div>
                {previewSavings != null && previewSavings > 0 && (
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                    <Sparkles className="h-3.5 w-3.5" />
                    {t(
                      `Member saves ${previewSavings.toLocaleString()} EGP`,
                      `يوفر العضو ${previewSavings.toLocaleString()} ج.م`
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            {!logged ? (
              <div className="space-y-2 pt-1">
                {selectedOffer?.claim && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <p className="font-semibold inline-flex items-center gap-1">
                      <Ticket className="h-3 w-3" />
                      {t("This redeems the member's claim.", "هذا سيُستخدم حجز العضو.")}
                    </p>
                    <p className="text-amber-800/90 mt-0.5">
                      {t(
                        "Once logged, the deal moves to 'Redeemed' and savings are locked in.",
                        "بعد التسجيل، يتحول العرض إلى \"مستخدم\" ويُثبَّت مبلغ التوفير."
                      )}
                    </p>
                  </div>
                )}
                <div className="flex gap-3">
                  <Button
                    onClick={onLogVisit}
                    disabled={logging || member.is_expired || !member.is_active}
                    className="flex-1"
                    data-testid="log-visit-btn"
                  >
                    {logging
                      ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      : <ClipboardCheck className="h-4 w-4 mr-2" />}
                    {selectedOffer?.claim
                      ? t("Redeem & log visit", "استخدام وتسجيل الزيارة")
                      : selectedOffer
                        ? t("Log visit with deal", "تسجيل الزيارة مع العرض")
                        : t("Log visit (no deal)", "تسجيل زيارة (بدون عرض)")}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={reset} title={t("New scan", "فحص جديد")}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 pt-1">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
                    <CheckCircle2 className="h-5 w-5" />
                    {t("Visit logged successfully!", "تم تسجيل الزيارة بنجاح!")}
                  </div>
                  {loggedSavings != null && loggedSavings > 0 && (
                    <p className="mt-1.5 text-xs text-emerald-700/90 inline-flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3" />
                      {t(
                        `Saved ${loggedSavings.toLocaleString()} EGP for this member`,
                        `تم توفير ${loggedSavings.toLocaleString()} ج.م للعضو`
                      )}
                    </p>
                  )}
                </div>
                <Button variant="outline" className="w-full" onClick={reset}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {t("Verify Next Member", "التحقق من عضو آخر")}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
